import { SparkleIcon, SpinnerIcon } from '@phosphor-icons/react'
import { useAISettingsStore, useAutoDJSettingsStore } from './store.ts'
import { Modal } from './Modal.tsx'

interface AIAutoDJModalProps {
  isOpen: boolean
  onClose: () => void
  onStart: (
    message: string,
    secondsToSwap: number,
    mixDuration: number,
    temperature: number,
    topP: number,
    model: string,
  ) => void
  onStartSimilar: (
    secondsToSwap: number,
    mixDuration: number,
    temperature: number,
    topP: number,
    model: string,
  ) => void
  onStartSimilarA: (
    secondsToSwap: number,
    mixDuration: number,
    temperature: number,
    topP: number,
    model: string,
  ) => void
  onStartSimilarB: (
    secondsToSwap: number,
    mixDuration: number,
    temperature: number,
    topP: number,
    model: string,
  ) => void
  isRunning: boolean
  hasTracksAB: boolean
  hasTrackA: boolean
  hasTrackB: boolean
}

export function AIAutoDJModal({
  isOpen,
  onClose,
  onStart,
  onStartSimilar,
  onStartSimilarA,
  onStartSimilarB,
  isRunning,
  hasTracksAB,
  hasTrackA,
  hasTrackB,
}: AIAutoDJModalProps) {
  const { secondsToSwap, mixDuration, message, setSecondsToSwap, setMixDuration, setMessage } =
    useAutoDJSettingsStore()
  const { temperature, topP, model, setTemperature, setTopP, setModel } = useAISettingsStore()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="AI Auto DJ"
      icon={<SparkleIcon weight="regular" size={20} className="text-pink-500" />}
      disabled={isRunning}
      width="w-3xl"
    >
      <div className="grid grid-cols-[2fr_1fr] gap-6">
        {/* Left column: Controls and buttons */}
        <div className="space-y-4">
          {/* Seconds to swap and Mix duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-row items-center gap-2">
              <label className="text-white font-semibold whitespace-nowrap">Seconds to swap</label>
              <input
                type="number"
                min="10"
                value={secondsToSwap}
                onChange={e => {
                  const value = parseInt(e.target.value)
                  if (!isNaN(value) && value >= 10) {
                    setSecondsToSwap(value)
                  }
                }}
                disabled={isRunning}
                className="flex-1 w-0 px-3 py-2 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded text-white text-sm focus:outline-none focus:border-[#666]"
              />
            </div>
            <div className="flex flex-row items-center gap-2">
              <label className="text-white font-semibold whitespace-nowrap">Mix duration</label>
              <input
                type="number"
                min="1"
                value={mixDuration}
                onChange={e => {
                  const value = parseInt(e.target.value)
                  if (!isNaN(value) && value >= 1) {
                    setMixDuration(value)
                  }
                }}
                disabled={isRunning}
                className="flex-1 w-0 px-3 py-2 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded text-white text-sm focus:outline-none focus:border-[#666]"
              />
            </div>
          </div>

          {/* Mood description with Start Auto DJing button */}
          <div className="space-y-2">
            <label className="text-white font-semibold">Describe the mood or genre</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              disabled={isRunning}
              placeholder="e.g., jungle, dark, energetic, dreamy..."
              className="w-full h-24 px-3 py-2 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded text-white text-sm resize-none focus:outline-none focus:border-[#666]"
            />
            <button
              onClick={() => {
                if (message.trim() && !isRunning) {
                  onStart(message.trim(), secondsToSwap, mixDuration, temperature, topP, model)
                }
              }}
              disabled={isRunning || !message.trim()}
              className="w-full px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isRunning ? (
                <>
                  <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                  <span>Auto DJing...</span>
                </>
              ) : (
                <>
                  <SparkleIcon weight="regular" size={16} />
                  <span>Start DJing on the Mood</span>
                </>
              )}
            </button>
          </div>

          {(hasTrackA && !hasTrackB) && (
            <button
              onClick={() => {
                if (!isRunning) {
                  onStartSimilarA(secondsToSwap, mixDuration, temperature, topP, model)
                }
              }}
              disabled={isRunning}
              className="w-full px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isRunning ? (
                <>
                  <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                  <span>Auto DJing...</span>
                </>
              ) : (
                <>
                  <SparkleIcon weight="regular" size={16} />
                  <span>Start DJing similar to A</span>
                </>
              )}
            </button>
          )}

          {(!hasTrackA && hasTrackB) && (
            <button
              onClick={() => {
                if (!isRunning) {
                  onStartSimilarB(secondsToSwap, mixDuration, temperature, topP, model)
                }
              }}
              disabled={isRunning}
              className="w-full px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isRunning ? (
                <>
                  <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                  <span>Auto DJing...</span>
                </>
              ) : (
                <>
                  <SparkleIcon weight="regular" size={16} />
                  <span>Start DJing similar to B</span>
                </>
              )}
            </button>
          )}

          {hasTracksAB && (
            <button
              onClick={() => {
                if (!isRunning) {
                  onStartSimilar(secondsToSwap, mixDuration, temperature, topP, model)
                }
              }}
              disabled={isRunning}
              className="w-full px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isRunning ? (
                <>
                  <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                  <span>Auto DJing...</span>
                </>
              ) : (
                <>
                  <SparkleIcon weight="regular" size={16} />
                  <span>Start DJing similar to A and B tracks</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Right column: Model parameters */}
        <div className="space-y-4">
          {/* Model selector */}
          <div className="space-y-2">
            <label className="text-white text-sm font-medium">Model</label>
            <select
              value={model}
              onChange={e =>
                setModel(e.target.value as 'gpt-5-chat-latest' | 'chatgpt-4o-latest')
              }
              disabled={isRunning}
              className="w-full px-3 py-2 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded text-white text-sm focus:outline-none focus:border-[#666]"
              style={{ colorScheme: 'dark' }}
            >
              <option value="gpt-5-chat-latest" className="bg-neutral-900 text-white">
                gpt-5-chat-latest
              </option>
              <option value="chatgpt-4o-latest" className="bg-neutral-900 text-white">
                chatgpt-4o-latest
              </option>
            </select>
          </div>

          {/* Temperature control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-white text-sm font-medium">Temperature</label>
              <span className="text-[#888] text-sm">{temperature.toFixed(1)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={temperature}
              onChange={e => setTemperature(parseFloat(e.target.value))}
              disabled={isRunning}
              className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
            />
            <div className="flex justify-between text-xs text-[#666]">
              <span>Focused</span>
              <span>Creative</span>
            </div>
          </div>

          {/* Top P control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-white text-sm font-medium">Top P</label>
              <span className="text-[#888] text-sm">{topP.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={topP}
              onChange={e => setTopP(parseFloat(e.target.value))}
              disabled={isRunning}
              className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
            />
            <div className="flex justify-between text-xs text-[#666]">
              <span>Narrow</span>
              <span>Broad</span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
